import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Bars3Icon, ChevronDoubleLeftIcon, ChevronDoubleRightIcon, Cog6ToothIcon } from '@heroicons/react/24/outline';
import { MdOutlineLogout } from 'react-icons/md';
import { FiUser } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import { useLocale } from '../i18n/LocaleProvider';

export default function AppTopBar({
    onMobileMenuOpen,
    onCompactToggle,
    isCompact,
    onLogout,
    showMobileMenu,
}) {
    const { t } = useTranslation();
    const { lang, setLang, langs } = useLocale();
    const [userOpen, setUserOpen] = useState(false);
    const userRef = useRef(null);

    useEffect(() => {
        const onDocClick = (e) => {
            if (userRef.current && !userRef.current.contains(e.target)) {
                setUserOpen(false);
            }
        };
        document.addEventListener('click', onDocClick);
        return () => document.removeEventListener('click', onDocClick);
    }, []);

    return (
        <header className="sticky top-0 z-[1020] flex items-center justify-between gap-4 px-4 md:px-6 h-[60px] bg-white/85 backdrop-blur-md border-b border-slate-200/80 shadow-[0_1px_0_rgba(15,23,42,0.04)]">
            <div className="flex items-center gap-2 md:gap-3 min-w-0">
                {showMobileMenu && (
                    <button
                        type="button"
                        onClick={onMobileMenuOpen}
                        className="xl:hidden inline-flex items-center justify-center w-10 h-10 rounded-xl text-slate-600 hover:bg-slate-100 transition-colors"
                        aria-label={t('common.openMenu')}
                    >
                        <Bars3Icon className="w-6 h-6" />
                    </button>
                )}

                <button
                    type="button"
                    onClick={onCompactToggle}
                    className="hidden xl:inline-flex items-center justify-center w-10 h-10 rounded-xl text-slate-500 hover:bg-slate-100 hover:text-slate-800 transition-colors"
                    aria-label={t('common.settings')}
                    title={isCompact ? t('common.expandSidebar') : t('common.collapseSidebar')}
                >
                    {isCompact ? (
                        <ChevronDoubleRightIcon className="w-5 h-5" />
                    ) : (
                        <ChevronDoubleLeftIcon className="w-5 h-5" />
                    )}
                </button>

                <Link
                    to="/"
                    className="flex items-center min-w-0 pl-1 ml-1 sm:border-l sm:border-slate-200 sm:pl-3"
                >
                    <img src="/logomain.svg" alt="MedPronix" className="h-8 w-auto max-w-[160px] object-contain object-left" />
                </Link>
            </div>

            <div className="relative flex items-center gap-2 shrink-0" ref={userRef}>
                <button
                    type="button"
                    onClick={() => setUserOpen((v) => !v)}
                    className={`inline-flex items-center gap-2 pl-1.5 pr-3 py-1.5 rounded-full border transition-all ${
                        userOpen
                            ? 'border-primary-200 bg-primary-50/80 shadow-sm'
                            : 'border-slate-200 bg-slate-50/80 hover:border-slate-300 hover:bg-white'
                    }`}
                >
                    <span className="w-8 h-8 rounded-full bg-[#1D2C7B] text-white flex items-center justify-center shadow-sm">
                        <FiUser className="w-4 h-4" />
                    </span>
                    <span className="hidden md:inline text-sm font-medium text-slate-700 max-w-[120px] truncate">
                        {t('common.profile')}
                    </span>
                </button>

                {userOpen && (
                    <div className="absolute right-0 top-[calc(100%+8px)] w-56 rounded-xl border border-slate-200 bg-white shadow-lg py-1 z-50">
                        <div className="px-3 pt-2 pb-1.5">
                            <p className="m-0 mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                                {t('common.language')}
                            </p>
                            <div className="flex gap-1">
                                {langs.map((item) => (
                                    <button
                                        key={item.code}
                                        type="button"
                                        onClick={() => setLang(item.code)}
                                        className={`flex-1 rounded-lg px-1.5 py-1 text-[11px] font-semibold transition-colors ${
                                            lang === item.code
                                                ? 'bg-[#1D2C7B] text-white'
                                                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                        }`}
                                    >
                                        {item.short}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <Link
                            to="/settings"
                            onClick={() => setUserOpen(false)}
                            className="mt-1 w-full flex items-center gap-2 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                        >
                            <Cog6ToothIcon className="w-5 h-5 shrink-0" />
                            {t('common.settings')}
                        </Link>
                        <button
                            type="button"
                            onClick={() => {
                                setUserOpen(false);
                                onLogout();
                            }}
                            className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-slate-700 hover:bg-red-50 hover:text-red-700 transition-colors text-left"
                        >
                            <MdOutlineLogout className="w-5 h-5 shrink-0" />
                            {t('common.logout')}
                        </button>
                    </div>
                )}
            </div>
        </header>
    );
}
